import { useEMTDispatch } from '@/store'
import { useCallback, useState } from 'react'
import { useNotifications } from '@/modules/Notifications'
import { useTranslation } from 'next-i18next'
import { gtmSendCustomEvent } from '@/helpers/GTMHelper'
import { Constants } from '@/assets'
import { useIsQueryOwner } from '@/hooks'
import { dispatchDeleteFavoriteAsync, dispatchPostFavoriteAsync } from '@/modules/Favorites/FavoritesServiceHelper'
import { IRI } from 'emt-library'
import useCurrentUser from '@/hooks/useCurrentUser'

enum GtmFavoriteEvent {
  add = 'member_favorite_sent',
  delete = 'member_favorite_delete',
}

export const useFavoritesV2 = (profileId: string, favoriteIRI: IRI | undefined) => {
  const { t } = useTranslation('common')

  const dispatch = useEMTDispatch()
  const [addNotificationError, addNotificationSuccess] = useNotifications()
  const { currentUnknownUser } = useCurrentUser()
  const [isQueryOwner] = useIsQueryOwner()

  // Inner state
  const [isLoading, setIsLoading] = useState(false)

  const handleError = (error: any) => {
    setIsLoading(false)
    addNotificationError({ error })
  }

  const handleSuccess = (gtmEvent: GtmFavoriteEvent) => {
    // Add success notification
    addNotificationSuccess({
      title: t(`alerts.favorites.${gtmEvent === GtmFavoriteEvent.add ? 'addSuccess' : 'deleteSuccess'}`) as string,
    })

    // Send GTM event
    if (currentUnknownUser)
      gtmSendCustomEvent(gtmEvent, {
        member_role_variable: isQueryOwner ? 'maitre' : 'emprunteur',
        member_id_variable: currentUnknownUser.id,
        targeted_member_id_variable: profileId,
        brand_variable: Constants.storageID,
        website_variable: Constants.rootUrl,
      })

    setIsLoading(false)
  }

  const addFavorite = useCallback(async () => {
    if (favoriteIRI) {
      try {
        setIsLoading(true)
        await dispatchPostFavoriteAsync(dispatch)(favoriteIRI)

        handleSuccess(GtmFavoriteEvent.add)

        return true
      } catch (error) {
        handleError(error)
      }
    }
    return false
  }, [favoriteIRI])

  const deleteFavorite = useCallback(async () => {
    if (favoriteIRI) {
      try {
        setIsLoading(true)
        await dispatchDeleteFavoriteAsync(dispatch)(favoriteIRI)

        handleSuccess(GtmFavoriteEvent.delete)

        return true
      } catch (error) {
        handleError(error)
      }
    }
    return false
  }, [favoriteIRI])

  const generateFavorite = useCallback(
    async (favorite: boolean) => {
      return favorite ? await deleteFavorite() : await addFavorite()
    },
    [addFavorite, deleteFavorite],
  )

  return [generateFavorite, isLoading] as const
}
